<template>
  <div
      class="io-sidebar-group"
      :data-level="level"
      :class="{
        'open': isOpen,
        'has-children': hasChildren,
        'active': item.active,
      }"
  >
    <IoRouterLink
        :link-url="item.url"
        :link-target="item.target"
        :link-title="item.titleAttr"
        :custom-handler="openOrMove"
    >
      <span
          v-if="item.icon"
          class="sidebar__icon"
          :style="`background-image: url('${item.icon}')`"
      />
      <span
          class="sidebar__label"
          v-html="item.title"
      />
      <span
          v-if="hasChildren"
          class="sidebar__chevron"
      >
        <FontAwesomeIcon :icon="faChevronDown" />
      </span>
    </IoRouterLink>
    <div
        v-if="hasChildren"
        class="io-sidebar-group__children"
    >
      <Expansion
          class="io-sidebar-group__children-wrapper"
          :expanded="isOpen"
      >
        <SidebarGroup
            v-for="(child, childInx) in item.children"
            :key="`sg-${level}-${index}-${childInx}`"
            :item="child"
            :level="level + 1"
            ref="sidebarGroups"
            @open="closeChildren(childInx)"
        />
      </Expansion>
    </div>
  </div>
</template>

<script lang="ts" setup>
import MenuInterface from '@/interfaces/MenuInterface';
import {
  ref,
  computed,
  watch,
  onBeforeUpdate,
} from 'vue';
import { useStore } from 'vuex';
import Expansion from '@/components/Expansion.vue';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';

library.add(faChevronDown);

const emit = defineEmits(['open']);
const props = withDefaults(defineProps<{
  item: MenuInterface
  level?: number
  index?: number
}>(), {
  level: 1,
  index: 1,
});

const store = useStore();
const isOpen = ref(false);
const hasChildren = computed(() => Object.values(props.item.children || {}).length > 0);
const sidebarGroups = ref([]);

const toggleSidebar = () => {
  store.dispatch('sidebar/toggle', 'sidebar');
};

const openOrMove = (move: { (): void }) => {
  if (!hasChildren.value || (hasChildren.value && isOpen.value)) {
    toggleSidebar();
    move();
  } else {
    isOpen.value = true;
  }
};

watch(() => props.item, () => {
  isOpen.value = !!props.item.active;
}, {
  deep: true,
});

onBeforeUpdate(() => {
  sidebarGroups.value = [];
});

const closeChildren = (keepInx: number | null = null) => {
  sidebarGroups.value.forEach((sidebarGroup, groupInx) => {
    const sg = sidebarGroup as { close: () => void };
    if (groupInx !== keepInx) {
      sg.close();
    }
  });
};

const close = () => {
  closeChildren();
  isOpen.value = false;
};

defineExpose({
  close,
});

watch(() => isOpen.value, () => {
  if (isOpen.value) {
    emit('open');
  }
});
</script>

<style lang="scss">
@use "sass:math";
@import 'src/scss/component-helpers';

.io-sidebar-group {
  font-size: 1rem;

  > a {
    font-size: inherit;
    padding: 0 1.25em;
    min-height: 2.8125em;
    display: grid;
    width: 100%;
    text-decoration: none;
    color: #38383A;
    text-align: left;
    line-height: 1.5625;
    grid-template-columns: auto 1fr auto;
    align-items: center;

    &:hover, &:focus {
      text-decoration: none;
    }

    &.active {
      background-color: transparent !important;
    }

    > .sidebar {
      &__icon {
        grid-column: 1 / 2;
        padding-right: 20px;
      }

      &__label {
        grid-column: 2 / 3;
        padding-right: 20px;
        padding-top: .625em;
        padding-bottom: .625em;
      }

      &__chevron {
        grid-column: 3 / 4;
        line-height: 1;

        > i, > svg {
          transform: rotateZ(0deg);
          transition: transform .5s ease-in-out;
        }
      }
    }
  }

  &[data-level="1"] {
    > a {
      border-bottom: 2px solid #F7F7F7;
    }
  }

  &[data-level="2"] {
    > a {
      padding-left: calc(.5em + 30px);
      background-color: rgba(231, 241, 245, .45);
      border-bottom: 2px solid #E7F1F5;

      &:before {
        width: .5em;
      }
    }
  }

  &[data-level="3"] {
    > a {
      padding-left: calc(1em + 30px);
      background-color: rgba(231, 241, 245, .45);
      border-bottom: 2px solid #E7F1F5;

      &:before {
        width: 1em;
      }
    }
  }

  > .io-sidebar-group__children {
    > .io-sidebar-group__children-wrapper {
      position: relative;

      &:before,
      &:after {
        content: '';
        display: block;
        width: 100%;
        height: 10px;
        background: linear-gradient(180deg, #000000 0%, rgba(0, 0, 0, 0) 100%);
        left: 0;
        top: -0.063rem;
        position: absolute;
        pointer-events: none;
        opacity: 0;
        transition: opacity .5s ease-in;
      }

      &:after {
        top: inherit;
        bottom: 0;
        transform: rotateZ(180deg);
      }
    }
  }

  &.open {
    > a {
      border-color: transparent;

      .sidebar__chevron {
        > i, > svg {
          transform: rotateZ(-90deg);
        }
      }
    }

    > .io-sidebar-group__children {
      > .io-sidebar-group__children-wrapper {
        &:before,
        &:after {
          opacity: 0.1;
        }
      }
    }
  }
}
</style>
